import { useId, useMemo, useState, type ReactNode } from 'react';

/**
 * Charts, drawn by hand.
 *
 * No charting library. The three shapes this product needs — a series over
 * months, a ranked bar, a sparkline — are a few dozen lines of SVG each, whereas
 * every general-purpose library arrives with its own canvas layer, its own theme
 * system fighting our CSS variables, and a bundle larger than the console. The
 * same reasoning that keeps the invoice PDF hand-written applies here.
 *
 * The house style is instrument, not infographic: hairline rules, one lit series,
 * numbers set in the mono face. A dashboard an operator reads every morning
 * should look like a gauge they trust, not a pitch deck.
 *
 * Every chart is drawn in a fixed user-space viewBox and scaled by CSS. Strokes
 * carry vector-effect="non-scaling-stroke" so a hairline stays a hairline at any
 * width instead of fattening as the card grows.
 */

const W = 640;
const H = 200;
const PAD = { top: 16, right: 8, bottom: 26, left: 8 };

export interface TrendPoint {
  /** 'YYYY-MM'. */
  month: string;
  value: number;
}

/** 'YYYY-MM' → 'Mar' / 'Mar 25' at the year boundary, which is where the eye needs it. */
function monthLabel(month: string, locale: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Date.UTC(Number(year), Number(m) - 1, 1));
  const short = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(date);
  return m === '01' ? `${short} ${String(year).slice(2)}` : short;
}

/**
 * A filled line over months.
 *
 * The area under the line is a very low-opacity wash of the same colour rather
 * than a gradient to transparent: at these heights a gradient reads as a smudge,
 * and the point of the fill is only to say which side of the line is "under".
 */
export function TrendChart({
  points,
  format,
  label,
  locale = 'en',
  color = 'var(--signal)',
  height = 200,
}: {
  points: TrendPoint[];
  /** Renders a value for the readout and the accessible table. */
  format: (value: number) => string;
  /** What this series is, for screen readers and the hover readout. */
  label: string;
  locale?: string;
  color?: string;
  height?: number;
}) {
  const gradientId = useId();
  const [active, setActive] = useState<number | null>(null);

  const geometry = useMemo(() => {
    if (points.length === 0) return null;
    const max = Math.max(...points.map((p) => p.value), 1);
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    // A single point has no span to divide by; centre it rather than divide by zero.
    const step = points.length > 1 ? innerW / (points.length - 1) : 0;
    const xs = points.map((_, i) => (points.length > 1 ? PAD.left + i * step : PAD.left + innerW / 2));
    const ys = points.map((p) => PAD.top + innerH - (p.value / max) * innerH);
    const line = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i]!.toFixed(1)}`).join(' ');
    const area = `${line} L${xs[xs.length - 1]!.toFixed(1)},${(H - PAD.bottom).toFixed(1)} L${xs[0]!.toFixed(1)},${(H - PAD.bottom).toFixed(1)} Z`;
    return { max, xs, ys, line, area, step, innerW };
  }, [points]);

  if (!geometry) return null;

  const shown = active ?? points.length - 1;
  const current = points[shown]!;
  // Label every month when there is room, otherwise thin them out so they never collide.
  const labelEvery = Math.ceil(points.length / 8);

  return (
    <div className="w-full">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span
          className="text-[0.625rem] font-semibold uppercase tracking-[0.18em]"
          style={{ color: 'var(--color-faint, var(--chalk-faint))' }}
        >
          {monthLabel(current.month, locale)}
        </span>
        <span className="tabular font-display text-[1.05rem] font-bold" style={{ color }}>
          {format(current.value)}
        </span>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${label}. ${points.map((p) => `${p.month}: ${format(p.value)}`).join('. ')}`}
        onMouseLeave={() => setActive(null)}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.20" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Baseline only. A full grid competes with the line for the same ink. */}
        <line
          x1={PAD.left}
          y1={H - PAD.bottom}
          x2={W - PAD.right}
          y2={H - PAD.bottom}
          stroke="var(--color-line, var(--hairline))"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />

        <path d={geometry.area} fill={`url(#${gradientId})`} />
        <path
          d={geometry.line}
          fill="none"
          stroke={color}
          strokeWidth="1.75"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        {/* The travelling marker: the one lit point, matching the signal-bar idiom. */}
        <line
          x1={geometry.xs[shown]}
          y1={PAD.top - 6}
          x2={geometry.xs[shown]}
          y2={H - PAD.bottom}
          stroke={color}
          strokeWidth="1"
          strokeOpacity="0.35"
          vectorEffect="non-scaling-stroke"
        />
        <circle cx={geometry.xs[shown]} cy={geometry.ys[shown]} r="3.5" fill={color} />

        {points.map((point, i) => (
          <g key={point.month}>
            {i % labelEvery === 0 && (
              <text
                x={geometry.xs[i]}
                y={H - PAD.bottom + 16}
                textAnchor="middle"
                fontSize="11"
                fill="var(--color-faint, var(--chalk-faint))"
                style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)' }}
              >
                {monthLabel(point.month, locale)}
              </text>
            )}
            {/* A full-height hit target per month: aiming at a 3px dot is not a control. */}
            <rect
              x={geometry.xs[i]! - (geometry.step || geometry.innerW) / 2}
              y={0}
              width={geometry.step || geometry.innerW}
              height={H - PAD.bottom}
              fill="transparent"
              onMouseEnter={() => setActive(i)}
            />
          </g>
        ))}
      </svg>
    </div>
  );
}

/**
 * A ranked horizontal bar — the shape for "which of these is working".
 *
 * Horizontal because the labels are route names, and a vertical bar chart either
 * truncates them or turns them on their side. Neither is readable at a glance,
 * which is the only thing this chart is for.
 */
export function BarRows({
  rows,
  color = 'var(--signal)',
  emptyLabel,
}: {
  rows: Array<{ key: string; label: string; value: number; hint?: string; caption?: string }>;
  color?: string;
  emptyLabel: string;
}) {
  const max = Math.max(...rows.map((row) => row.value), 1);
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-[0.8125rem]" style={{ color: 'var(--color-faint, var(--chalk-faint))' }}>
        {emptyLabel}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2.5">
      {rows.map((row) => (
        <li key={row.key}>
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <span className="truncate text-[0.8125rem]" style={{ color: 'var(--color-text, var(--chalk))' }}>
              {row.label}
            </span>
            <span
              className="tabular shrink-0 text-[0.8125rem] font-semibold"
              style={{ color: 'var(--color-text, var(--chalk))' }}
            >
              {row.hint}
            </span>
          </div>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full"
            style={{ background: 'var(--color-line, var(--hairline))' }}
          >
            <div
              className="h-full rounded-full"
              style={{ width: `${Math.max((row.value / max) * 100, 1.5)}%`, background: color }}
            />
          </div>
          {row.caption && (
            <p className="mt-1 text-[0.6875rem]" style={{ color: 'var(--color-faint, var(--chalk-faint))' }}>
              {row.caption}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

/** A series small enough to sit inside a table cell. No axis, no readout. */
export function Sparkline({
  values,
  label,
  color = 'var(--signal)',
  width = 96,
  height = 24,
}: {
  values: number[];
  label: string;
  color?: string;
  width?: number;
  height?: number;
}) {
  if (values.length === 0) return null;
  const max = Math.max(...values, 1);
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((value, i) => {
    const x = values.length > 1 ? i * step : width / 2;
    const y = height - 2 - (value / max) * (height - 4);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={label}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <path
        d={points.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** A titled panel, so a chart and its heading are one object on the page. */
export function ChartCard({
  title,
  hint,
  action,
  children,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      className="rounded-xl border p-4"
      style={{
        background: 'var(--color-bg, var(--ink-850))',
        borderColor: 'var(--color-line, var(--hairline))',
      }}
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2
            className="text-[0.625rem] font-semibold uppercase tracking-[0.18em]"
            style={{ color: 'var(--color-faint, var(--chalk-faint))' }}
          >
            {title}
          </h2>
          {hint && (
            <p className="mt-1 text-[0.75rem]" style={{ color: 'var(--color-muted, var(--chalk-dim))' }}>
              {hint}
            </p>
          )}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}
